// PPTHeroSceneManager.js - Secure scene selection with company email verification
import { auth } from "../../firebaseConfig.js";
import { checkFirmAccess, getCompanyEmailStatus } from "../../services/firmAuthService.js";

export default class PPTHeroSceneManager {
  // Cache for verification results to avoid repeated checks
  static _verificationCache = new Map();
  static _cacheExpiry = 5 * 60 * 1000; // 5 minutes

  static async getUserFirm() {
    const user = auth.currentUser;
    if (!user) return 'generic';
    
    // Check cache first
    const cacheKey = user.uid;
    const cached = this._verificationCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this._cacheExpiry) {
      return cached.firm;
    }

    try {
      // SECURE: Check actual company email verification
      const hasVerifiedCompanyEmail = await checkFirmAccess(user.uid);
      console.log('🔍 DEBUG: hasVerifiedCompanyEmail =', hasVerifiedCompanyEmail);
      
      if (hasVerifiedCompanyEmail) {
        // Get company email status to determine firm
        const status = await getCompanyEmailStatus(user.uid);
        console.log('🔍 DEBUG: Company email status =', status);
        const domain = status.domain?.toLowerCase();
        console.log('🔍 DEBUG: Domain =', domain);
        
        // Determine firm based on verified company email domain
        let firm = 'generic';
        if (domain && domain.includes('bain')) {
          firm = 'bain';
        }
        // OTHER FIRMS: Recognized but route to "coming soon" experience
        else if (domain && (domain.includes('mckinsey') || domain.includes('bcg') || 
                           domain.includes('accenture') || domain.includes('deloitte') || 
                           domain.includes('ey') || domain.includes('kpmg') || domain.includes('pwc'))) {
          firm = 'other-firm'; // Special flag for non-Bain verified firms
        }
        
        // Cache the result
        this._verificationCache.set(cacheKey, {
          firm: firm,
          timestamp: Date.now()
        });
        
        return firm;
      } else {
        // No verified company email - return generic
        this._verificationCache.set(cacheKey, {
          firm: 'generic',
          timestamp: Date.now()
        });
        return 'generic';
      }
    } catch (error) {
      console.error("Error checking firm access:", error);
      // Fail safely to generic mode
      return 'generic';
    }
  }

  // Clear verification cache (useful for testing or when email status changes)
  static clearVerificationCache() {
    this._verificationCache.clear();
  }

  static async getSceneKey(gameMode = 'firm') {
    const userFirm = await this.getUserFirm();
    
    const sceneMap = {
      'bain': {
        'generic': 'PPTHeroGenericScene',
        'firm': 'PPTHeroBainScene'
      },
      'other-firm': {
        'generic': 'PPTHeroGenericScene', 
        'firm': 'PPTHeroGenericScene' // Route to generic but with "coming soon" message
      },
      'generic': {
        'generic': 'PPTHeroGenericScene',
        'firm': 'PPTHeroGenericScene' // No firm access, stay generic
      }
    };
    
    const firmScenes = sceneMap[userFirm] || sceneMap['generic'];
    return firmScenes[gameMode] || 'PPTHeroGenericScene';
  }

  static async startPPTHero(phaser, gameMode = 'firm', data = {}) {
    try {
      const userFirm = await this.getUserFirm();
      const sceneKey = await this.getSceneKey(gameMode);
      
      console.log(`Starting PPT Hero: User Firm: ${userFirm}, Mode: ${gameMode}, Scene: ${sceneKey}`);
      
      // CRITICAL FIX: Prevent duplicate scene creation
      if (phaser.scene && (phaser.scene.isActive('PPTHeroGenericScene') || phaser.scene.isActive('PPTHeroBainScene'))) {
        console.warn(`PPTHeroSceneManager: PPT Hero already running, ignoring duplicate start request`);
        return;
      }
      
      // Check if user tried to access firm mode but doesn't have full access
      if (gameMode === 'firm' && (userFirm === 'generic' || userFirm === 'other-firm')) {
        const user = auth.currentUser;
        if (user) {
          // Show appropriate message based on firm status
          await this.showAccessDeniedMessage(phaser, 'firm', userFirm);
          // Continue with generic scene
        }
      }
      
      const sceneData = {
        ...data,
        firmId: userFirm,
        gameMode: gameMode,
        hasVerifiedCompanyEmail: userFirm !== 'generic'
      };
      
      // ENHANCED: Try SceneManager first for professional transitions
      if (window.sceneManager && typeof window.sceneManager.switchToScene === 'function') {
        console.log(`PPTHeroSceneManager: Using SceneManager for professional transition to ${sceneKey}`);
        window.sceneManager.switchToScene(sceneKey, sceneData);
      } else {
        console.log(`PPTHeroSceneManager: Using direct Phaser scene transition to ${sceneKey}`);
        phaser.scene.start(sceneKey, sceneData);
      }
    } catch (error) {
      console.error("Error starting PPT Hero:", error);
      // Fallback to generic scene
      const fallbackData = { 
        ...data, 
        firmId: 'generic', 
        gameMode: 'generic',
        hasVerifiedCompanyEmail: false
      };
      
      if (window.sceneManager && typeof window.sceneManager.switchToScene === 'function') {
        window.sceneManager.switchToScene('PPTHeroGenericScene', fallbackData);
      } else {
        phaser.scene.start('PPTHeroGenericScene', fallbackData);
      }
    }
  }

  // Show clear message when firm access is denied
  static async showAccessDeniedMessage(phaser, requestedMode, firmStatus = 'generic') {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const status = await getCompanyEmailStatus(user.uid);
      let message = "";
      
      if (!status.companyEmail) {
        message = "Associate your company email in your profile to access firm-specific content.";
      } else if (!status.verified) {
        message = `Your company email (${status.companyEmail}) is pending verification. Check your inbox and click the verification link.`;
      } else if (firmStatus === 'other-firm') {
        // SPECIAL MESSAGE for verified non-Bain firms
        const domain = status.domain?.toUpperCase() || 'YOUR FIRM';
        message = `🏢 ${domain} Features Coming Soon!\n\nYour company email is verified, but ${domain}-specific training protocols and content are still in development.\n\nYou currently have access to our comprehensive generic training modules. Firm-specific features will be added soon!`;
      } else {
        message = "Your company email domain doesn't have access to this firm's content.";
      }

      // FIXED: Show message in current scene if possible
      try {
        // Get current scene using the correct Phaser API
        const sceneManager = phaser.scene;
        const currentScene = sceneManager.scene;
        
        if (currentScene && typeof currentScene.showMessage === 'function') {
          currentScene.showMessage(message, 4000, false);
        } else if (currentScene && typeof currentScene.showCenterNotification === 'function') {
          currentScene.showCenterNotification(message, 'warning', 5000);
        } else {
          // Fallback to console and alert
          console.warn("PPT Hero Access Denied:", message);
          alert("Access Restricted: " + message);
        }
      } catch (sceneError) {
        console.error("Error showing access denied in scene:", sceneError);
        // Fallback to console and alert
        console.warn("PPT Hero Access Denied:", message);
        alert("Access Restricted: " + message);
      }
    } catch (error) {
      console.error("Error showing access denied message:", error);
    }
  }

  static getCurrentMode() {
    return localStorage.getItem('pptHeroMode') || 'firm';
  }

  static setCurrentMode(mode) {
    localStorage.setItem('pptHeroMode', mode);
  }

  static toggleMode() {
    const currentMode = this.getCurrentMode();
    const newMode = currentMode === 'firm' ? 'generic' : 'firm';
    this.setCurrentMode(newMode);
    
    // Clear verification cache when toggling to ensure fresh firm access check
    if (newMode === 'firm') {
      this.clearVerificationCache();
      console.log('🔄 Cache cleared on toggle to firm mode - will re-check firm access');
    }
    
    return newMode;
  }

  static async getModeDisplayName(mode = null) {
    const actualMode = mode || this.getCurrentMode();
    const userFirm = await this.getUserFirm();
    
    if (actualMode === 'generic') {
      return 'Generic';
    } else {
      return userFirm === 'bain' ? 'Bain' : 'Firm';
    }
  }

  // Synchronous method for UI display when async check isn't feasible
  static getModeDisplayNameSync(mode = null) {
    const actualMode = mode || this.getCurrentMode();
    
    if (actualMode === 'generic') {
      return 'Generic';
    } else {
      // Default to 'Firm' without verification check - will show proper firm name when verification completes
      return 'Firm';
    }
  }
}




